import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './ManageTab.module.css';

/* One semester's config card: mode toggle, start date (structured only), and a
   live preview of what the next session's label would look like. */
function SemesterCard({ sem, onSave, onPreview }) {
  const [mode, setMode]           = useState(sem.label_mode || 'simple');
  const [startDate, setStartDate] = useState(sem.start_date || '');
  const [preview, setPreview]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [dirty, setDirty]         = useState(false);

  // Refresh the preview whenever mode or date changes (debounced lightly).
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const p = await onPreview(sem.semester_id, mode, startDate);
      if (!cancelled) setPreview(p);
    };
    run();
    return () => { cancelled = true; };
  }, [mode, startDate]); // eslint-disable-line

  const structuredNeedsDate = mode === 'structured' && !startDate;

  const save = async () => {
    setSaving(true);
    const ok = await onSave(sem.semester_id, mode, startDate);
    setSaving(false);
    if (ok) setDirty(false);
  };

  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '16px 18px', marginBottom: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--txt)' }}>
          {sem.name}
          {sem.is_active === false && (
            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--txt-faint)' }}> · inactive</span>
          )}
        </span>
        <span style={{ fontSize: 12, color: 'var(--txt-dim)' }}>
          Next session:{' '}
          <span style={{ color: 'var(--gold)', fontWeight: 600, fontFamily: 'monospace' }}>
            {preview ?? '…'}
          </span>
        </span>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {/* Mode toggle */}
        <div>
          <label style={{ fontSize: 11, color: 'var(--txt-faint)', display: 'block', marginBottom: 4 }}>
            Label style
          </label>
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { v: 'simple', l: 'Simple (1, 2, 3…)' },
              { v: 'structured', l: 'Structured (fa26.1.1.1)' },
            ].map(opt => (
              <button
                key={opt.v}
                className={mode === opt.v ? 'btn-primary' : 'btn-ghost'}
                onClick={() => { setMode(opt.v); setDirty(true); }}
                style={{ fontSize: 12, padding: '5px 10px' }}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>

        {/* Start date — only meaningful for structured */}
        {mode === 'structured' && (
          <div>
            <label style={{ fontSize: 11, color: 'var(--txt-faint)', display: 'block', marginBottom: 4 }}>
              Semester start (your first day)
            </label>
            <input
              type="date"
              value={startDate}
              onChange={e => { setStartDate(e.target.value); setDirty(true); }}
              style={{ fontSize: 13, padding: '4px 8px' }}
            />
          </div>
        )}

        <button
          className="btn-primary"
          onClick={save}
          disabled={saving || structuredNeedsDate || !dirty}
          style={{ fontSize: 13 }}
          title={structuredNeedsDate ? 'Structured labels need a start date' : ''}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {structuredNeedsDate && (
        <div style={{ fontSize: 11, color: 'var(--gold)', marginTop: 8 }}>
          Pick your first day of the term to use structured labels — week numbers
          are counted from it (Monday-based).
        </div>
      )}
      {mode === 'structured' && (
        <div style={{ fontSize: 11, color: 'var(--txt-faint)', marginTop: 8, lineHeight: 1.5 }}>
          Format is <code>semester.week.day.session</code> — e.g. <code>fa26.1.1.2</code> is
          the 2nd session on the Monday of week 1.
        </div>
      )}
    </div>
  );
}

export default function ManageSemesterTab() {
  const { apiFetch } = useAuth();
  const [semesters, setSemesters] = useState(null);
  const [flash, setFlash] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/courses/semester-setup?include_inactive=${showInactive}`);
      if (res.ok) setSemesters(await res.json());
      else setSemesters([]);
    } catch { setSemesters([]); }
  }, [apiFetch, showInactive]);

  useEffect(() => { load(); }, [load]);

  const save = async (semesterId, mode, startDate) => {
    try {
      const res = await apiFetch('/api/courses/semester-setup', {
        method: 'PUT',
        body: JSON.stringify({
          semester_id: semesterId,
          label_mode: mode,
          start_date: startDate || null,
        }),
      });
      if (res.ok) {
        setFlash('Saved.');
        setTimeout(() => setFlash(''), 2500);
        return true;
      }
      const d = await res.json().catch(() => ({}));
      setFlash(d.detail || 'Could not save.');
      setTimeout(() => setFlash(''), 4000);
      return false;
    } catch (e) {
      setFlash(e.message);
      return false;
    }
  };

  // Live preview. The backend preview reflects saved config, so for an unsaved
  // in-progress edit we compute a light client-side preview for immediacy, and
  // trust the backend value after a save. Here we call the backend preview
  // endpoint which reflects what's currently saved; the card also updates
  // optimistically on save.
  const preview = async (semesterId /*, mode, startDate */) => {
    try {
      const res = await apiFetch(`/api/courses/semester-setup/preview?semester_id=${semesterId}`);
      if (res.ok) {
        const d = await res.json();
        return d.preview;
      }
    } catch { /* ignore */ }
    return null;
  };

  return (
    <div className={styles.panel}>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Manage Semester</h3>
        <p style={{ fontSize: 12, color: 'var(--txt-dim)', marginBottom: 4, lineHeight: 1.5 }}>
          Set how your session IDs look, per semester. <strong>Simple</strong> just
          numbers them 1, 2, 3… and resets each term. <strong>Structured</strong> uses
          <code> semester.week.day.session</code> (like <code>fa26.1.1.2</code>) and needs
          your first day of the term to count weeks. Each instructor's numbering is
          independent — your session 1 and another instructor's session 1 don't collide.
        </p>
        {flash && (
          <div style={{ fontSize: 12, color: 'var(--green)', margin: '6px 0 12px' }}>{flash}</div>
        )}

        <label style={{
          fontSize: 12, color: 'var(--txt-dim)', display: 'flex', alignItems: 'center',
          gap: 6, cursor: 'pointer', marginTop: 4, marginBottom: 4,
        }}>
          <input type="checkbox" checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
            style={{ width: 15, height: 15 }} />
          Show inactive semesters
        </label>

        {semesters === null ? (
          <div style={{ fontSize: 13, color: 'var(--txt-faint)', marginTop: 12 }}>Loading…</div>
        ) : semesters.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--txt-dim)', marginTop: 12 }}>
            {showInactive
              ? "No semesters exist yet. An admin creates them in the Admin tab."
              : "No active semesters. Tick \u201cShow inactive semesters\u201d above, or ask an admin to activate one in the Admin tab."}
          </div>
        ) : (
          <div style={{ marginTop: 14 }}>
            {semesters.map(s => (
              <SemesterCard
                key={s.semester_id}
                sem={s}
                onSave={save}
                onPreview={preview}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
